<template>
  <d-card
    :class="['card-small d-inline-flex', { show: show }]"
    :style="{ top: `${positionTop}px`, left: `${positionLeft}px` }"
  >
    <d-card-body class="px-2 py-0">
      <d-button-group>
        <d-button
          @click="answerOption"
          theme="success"
          :title="$t('puzzle.option.answer')"
          ><i class="fas fa-exclamation-circle"></i
        ></d-button>
        <d-button
          @click="questionOption"
          :disabled="!cell.answer.id"
          theme="primary"
          :title="$t('puzzle.option.question')"
          ><i class="fas fa-question-circle"></i
        ></d-button>
        <d-button
          @click="groupOption"
          :disabled="cell.type !== 'col'"
          theme="info"
          :title="$t('puzzle.option.merge')"
          ><i class="far fa-object-group"></i
        ></d-button>
        <d-button
          @click="ungroupOption"
          theme="light"
          :title="$t('puzzle.option.split')"
          ><i class="far fa-object-ungroup"></i
        ></d-button>
        <d-button
          @click="arrowOption"
          theme="secondary"
          :title="$t('puzzle.option.arrows')"
          ><i class="fas fa-expand-arrows-alt"></i
        ></d-button>
        <d-button
          @click="cleanCell"
          theme="danger"
          :title="$t('puzzle.option.clearCell')"
          ><i class="fas fa-trash"></i
        ></d-button>
      </d-button-group>
    </d-card-body>
  </d-card>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import {
  CELL,
  CELL_SIZE,
  _PUZZLE_SET_CELL_,
  _PUZZLE_UNLINK_RELATED_QUESTIONS_,
  _PUZZLE_ARROW_OPTION_,
  _PUZZLE_SELECTED_CELL_,
  _PUZZLE_ANSWER_OPTION_,
  _PUZZLE_UNGROUP_OPTION_,
  _PUZZLE_CLOSE_ALL_MODAL_,
  _PUZZLE_QUESTION_OPTION_,
  _PUZZLE_PRE_GROUP_OPTION_,
  FONT_SIZE,
} from '../PuzzleModel';
import puzzleReadOnly from '../../mixins/puzzleReadOnly';

const SELECT_MODAL_POSITION_TOP = 0;
const SELECT_MODAL_POSITION_LEFT = 0;

const SELECT_CELL_NEXT_POSITION = 1;
const SELECT_CELL_OFFSET_TOP = 77;
const SELECT_CELL_RECT_OFFSET_TOP = 4;
const SELECT_CELL_OFFSET_LEFT = 139;

export default {
  name: 'puzzle-select-option-modal',
  mixins: [puzzleReadOnly],
  data() {
    return {
      positionTop: SELECT_MODAL_POSITION_TOP,
      positionLeft: SELECT_MODAL_POSITION_LEFT,
      cell: cloneDeep(CELL),
      show: false,
      cellRect: null,
    };
  },
  computed: {
    puzzle() {
      const { puzzle } = this.$store.state.puzzle;
      return puzzle;
    },
  },
  methods: {
    ...mapActions(['setPuzzleCell']),
    answerOption() {
      if (!this.guardPuzzleMutation()) return;
      this.show = false;
      Events.$emit(_PUZZLE_ANSWER_OPTION_, this.cell, this.cellRect);
    },
    questionOption() {
      if (!this.guardPuzzleMutation()) return;
      this.show = false;
      Events.$emit(_PUZZLE_QUESTION_OPTION_, this.cell, this.cellRect);
    },
    validCells() {
      const { puzzleCell } = this.$store.state.puzzle;
      if (puzzleCell === null) {
        return true;
      }
      if (
        this.cell.positionx === puzzleCell.positionx &&
        this.cell.positiony === puzzleCell.positiony
      ) {
        return false;
      }
      return true;
    },
    groupOption() {
      if (!this.guardPuzzleMutation()) {
        return undefined;
      }
      this.closeAll();
      if (this.validCells()) {
        this.show = false;
        this.cell.updated = true;
        Events.$emit(_PUZZLE_PRE_GROUP_OPTION_, this.cell);
        return this.setPuzzleCell(this.cell);
      }
      Events.$emit(_PUZZLE_SELECTED_CELL_, this.cell);
      this.cell.active = false;
      return this.setPuzzleCell(cloneDeep(CELL));
    },
    ungroupOption() {
      if (!this.guardPuzzleMutation()) return;
      this.show = false;
      this.closeAll();
      Events.$emit(_PUZZLE_UNGROUP_OPTION_, this.cell, this.cellRect);
    },
    arrowOption() {
      if (!this.guardPuzzleMutation()) return;
      this.show = false;
      this.closeAll();
      Events.$emit(_PUZZLE_ARROW_OPTION_, this.cell, this.cellRect);
    },
    closeAll() {
      Events.$emit(_PUZZLE_CLOSE_ALL_MODAL_);
    },
    changeCellHide(nextPosition = SELECT_CELL_NEXT_POSITION) {
      const { table } = this.$store.state.puzzle.puzzle;
      const index = table.findIndex(
        cell =>
          cell.positiony === this.cell.positiony &&
          cell.positionx === this.cell.positionx,
      );
      if (index >= 0) {
        if (table[index + nextPosition].hide) {
          table[index + nextPosition].updated = true;
          table[index + nextPosition].hide = false;
        }
      }
    },
    cleanParentCell(cell) {
      const [positionX] = cell.positionx.split('_');
      const [positionY] = cell.positiony.split('_');
      const index =
        parseInt(positionY, Number) * parseInt(this.puzzle.columns, Number) +
        parseInt(positionX, Number);
      this.cell = this.puzzle.table[index];
    },
    extractRowPosition(positiony) {
      return positiony.toString().split('_')[0];
    },
    unlinkCellTree(cell) {
      if (!cell) return;
      if (cell.children && cell.children.length) cell.children.forEach(child => this.unlinkCellTree(child));
      Events.$emit(_PUZZLE_UNLINK_RELATED_QUESTIONS_, cell);
    },
    cleanCell() {
      if (!this.guardPuzzleMutation()) return;

      this.unlinkCellTree(this.cell);

      if (this.cell.type === 'colspan') {
        this.changeCellHide(SELECT_CELL_NEXT_POSITION);
      }
      if (this.cell.type === 'rowspan') {
        const { columns } = this.$store.state.puzzle.puzzle;
        this.changeCellHide(columns);
      }
      if (this.cell.parent_id) {
        this.cleanParentCell(this.cell);
      }
      this.cell = {
        ...cloneDeep(CELL),
        ...{
          id: this.cell.id,
          positionx: this.cell.positionx,
          positiony: this.extractRowPosition(this.cell.positiony),
          parent_id: this.cell.parent_id,
          isQuestion: true,
          settings: {
            bold: true,
            font_size: FONT_SIZE,
            background: null,
          },
          updated: true,
        },
      };
      Events.$emit(_PUZZLE_SET_CELL_, this.cell);
    },
    isValidCell(cell) {
      return cell && cell.answer;
    },
    closeModal() {
      this.show = false;
    },
  },
  destroyed() {
    Events.$off(_PUZZLE_SELECTED_CELL_);
  },
  mounted() {
    Events.$on(_PUZZLE_SELECTED_CELL_, (cell, cellRect) => {
      if (this.isReadOnly || !this.isValidCell(cell)) {
        this.closeModal();
        return;
      }
      this.cell = cell;
      this.cellRect = cellRect || null;
      this.show = cell.active;
      if (cellRect) {
        this.positionTop =
          cellRect.top + cellRect.height + SELECT_CELL_RECT_OFFSET_TOP;
        this.positionLeft =
          cellRect.left + cellRect.width / 2 - SELECT_CELL_OFFSET_LEFT;
      } else {
        const { positiony: rawPositiony } = cell;
        const positiony = Number.isNaN(Number(rawPositiony))
          ? rawPositiony.split('_')[0]
          : rawPositiony;
        this.positionTop = positiony * CELL_SIZE + SELECT_CELL_OFFSET_TOP;
        this.positionLeft =
          cell.positionx * CELL_SIZE + CELL_SIZE / 2 - SELECT_CELL_OFFSET_LEFT;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.card-small {
  visibility: hidden;
  background-color: #d7d7d7;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1199;
  transition: all 0.5 ease-in;
  &:before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent #d7d7d7 transparent;
  }
  &.show {
    visibility: visible;
    transition: all 0.5 ease-in;
  }
}
</style>
